import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { CreateSportRequest, SportsGenre, Category, MediaLink } from '@/types/model';
import { useTranslation } from '@/hooks';
import { useCategoriesForDropdown } from '@/hooks';
import { Translation } from '@/locales';
import Input from '@/components/ui/Input';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';
import MultiSelect from '@/components/ui/MultiSelect';
import { sportsGenreOptions, qualityTypes } from '@/constants';
import LinkManager from '@/components/ui/LinkManager';
import MediaAssetsSection from '@/components/ui/MediaAssetsSection';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import { isDigitalOceanSpacesUrl } from '@/utils/validation.ts';
import { MediaSource } from '@/types/media';

const createSportSchema = (t: Translation) => z.object({
  title: z
    .string()
    .min(3, t.sports.validation.titleMinLength)
    .max(200, t.sports.validation.titleMaxLength),
  year: z
    .number({
      message: t.sports.validation.yearRequired
    })
    .min(1900, t.sports.validation.yearMin)
    .max(2100, t.sports.validation.yearMax),
  releaseDate: z.string().optional(),
  durationMinutes: z
    .number({
      message: t.sports.validation.durationRequired
    })
    .min(1, t.sports.validation.durationMin)
    .max(999, t.sports.validation.durationMax),
  spanishSynopsis: z
    .string()
    .max(10000, t.sports.validation.spanishSynopsisMax)
    .optional(),
  englishSynopsis: z
    .string()
    .max(10000, t.sports.validation.englishSynopsisMax)
    .optional(),
  genres: z.array(z.string()).optional(),
  categoryIds: z.array(z.string()).optional(),
  coverImageUrl: z
    .url(t.sports.validation.coverImageInvalid)
    .or(z.literal('')),
  backgroundImageUrl: z
    .url(t.sports.validation.backgroundImageInvalid)
    .or(z.literal('')),
  trailerUrl: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine((value) => !value || isDigitalOceanSpacesUrl(value), {
      message: 'Por favor suba el trailer a Digital Ocean',
    }),
  reference: z
    .string()
    .max(100, t.sports.validation.referenceMax)
    .optional(),
  active: z.boolean(),
  adultContent: z.boolean(),
  monoAudio: z.boolean(),
  englishOnly: z.boolean(),
  kidsContent: z.boolean(),
  links: z.array(
    z.object({
      id: z.string().optional(),
      quality: z.enum(qualityTypes),
      url: z.url(t.validation.urlFormat),
      audioLanguages: z.array(z.string()).optional(),
      subtitleLanguages: z.array(z.string()).optional(),
      active: z.boolean(),
    }), t.links.required
  ).min(1, t.links.required)
});

type SportFormData = z.infer<ReturnType<typeof createSportSchema>>;

interface SportsFormProps {
  onSuccess?: (data: CreateSportRequest) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateSportRequest>;
  sportId?: string;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const SportsForm = ({
  onSuccess,
  onCancel,
  initialData,
  isEdit = false,
  isSubmitting = false
}: SportsFormProps) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    initialData?.categoryIds || []
  );


  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<SportFormData>({
    resolver: zodResolver(createSportSchema(t)),
    defaultValues: {
      ...initialData,
      active: initialData?.active ?? true,
      adultContent: initialData?.adultContent ?? false,
      monoAudio: initialData?.monoAudio ?? false,
      englishOnly: initialData?.englishOnly ?? false,
      kidsContent: initialData?.kidsContent ?? false,
      genres: initialData?.genres || [],
      categoryIds: initialData?.categoryIds || []
    }
  });

  // Custom hook to filter categories by type for sports
  const useCategoriesForSportsDropdown = () =>
    useCategoriesForDropdown('SPORTS');
  const { data: categoriesData } = useCategoriesForSportsDropdown();

  const mapCategoriesToOptions = (data: unknown[]) =>
    (data as Category[]).map(cat => ({ value: cat.id, label: cat.description }));

  // Watch form values for image previews and genre selection
  const watchedCoverImage = watch('coverImageUrl');
  const watchedBackgroundImage = watch('backgroundImageUrl');
  const watchedTrailerUrl = watch('trailerUrl');
  const watchedGenres = watch('genres') || [];
  const watchedLinks = watch('links') || [];

  const handleBackgroundImageChange = (url: string) => {
    setValue('backgroundImageUrl', url);
  };

  const handleTrailerUrlChange = (url: string) => {
    setValue('trailerUrl', url);
  };

  const onSubmit = async (data: SportFormData) => {
    console.log("data", data);
    try {
      setSubmitError(null);
      const formData: CreateSportRequest = {
        ...data,
        year: Number(data.year),
        durationMinutes: Number(data.durationMinutes),
        genres: data.genres as SportsGenre[]
      };
      await onSuccess?.(formData);
      if (!isEdit) {
        reset();
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };


  const handleAddLink = async (link: Omit<MediaLink, 'id' | 'createdDate' | 'updatedDate'>) => {
    try {
      const newLink = {
        ...link,
        id: crypto.randomUUID(),
        audioLanguages: link.audioLanguages?.map(a=> a.toUpperCase()) || [],
        subtitleLanguages: link.subtitleLanguages?.map(s => s.toUpperCase()) || [],
      };
      setValue('links', [...watchedLinks, newLink]);
    } catch (error) {
      console.error('Error adding link:', error);
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    try {
      setValue('links', watchedLinks.filter(link => link.id !== linkId));
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  const handleUpdateLink = async (linkId: string, updatedLink: Omit<MediaLink, 'id' | 'createdDate' | 'updatedDate'>) => {
    try {
      const updatedLinks = watchedLinks.map(link =>
        link.id === linkId
          ? {
              ...link,
              ...updatedLink,
              audioLanguages: updatedLink.audioLanguages?.map(a => a.toUpperCase()) || [],
              subtitleLanguages: updatedLink.subtitleLanguages?.map(s => s.toUpperCase()) || [],
            }
          : link
      );
      setValue('links', updatedLinks);
    } catch (error) {
      console.error('Error updating link:', error);
    }
  };

  const handleToggleLink = async (linkId: string, active: boolean) => {
    try {
      const updatedLinks = watchedLinks.map(link =>
        link.id === linkId ? { ...link, active } : link
      );
      setValue('links', updatedLinks);
    } catch (error) {
      console.error('Error toggling link:', error);
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">
          {isEdit ? t.sports.editTitle : t.sports.createTitle}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {isEdit ? t.sports.editDescription : t.sports.createDescription}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
        {/* Basic Information Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label={t.sports.fields.title}
            placeholder={t.sports.placeholders.title}
            error={errors.title?.message}
            {...register('title')}
          />

          <Input
            label={t.sports.fields.year}
            type="number"
            placeholder={t.sports.placeholders.year}
            error={errors.year?.message}
            {...register('year', { valueAsNumber: true })}
          />

          <Input
            label={t.sports.fields.releaseDate}
            type="date"
            placeholder={t.sports.placeholders.releaseDate}
            error={errors.releaseDate?.message}
            {...register('releaseDate')}
          />

          <Input
            label={t.sports.fields.durationMinutes}
            type="number"
            placeholder={t.sports.placeholders.durationMinutes}
            error={errors.durationMinutes?.message}
            {...register('durationMinutes', { valueAsNumber: true })}
          />
        </div>

        {/* Synopsis Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.sports.fields.englishSynopsis}
            </label>
            <textarea
              rows={4}
              placeholder={t.sports.placeholders.englishSynopsis}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              {...register('englishSynopsis')}
            />
            {errors.englishSynopsis && (
              <p className="mt-1 text-sm text-red-600">{errors.englishSynopsis.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.sports.fields.spanishSynopsis}
            </label>
            <textarea
              rows={4}
              placeholder={t.sports.placeholders.spanishSynopsis}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              {...register('spanishSynopsis')}
            />
            {errors.spanishSynopsis && (
              <p className="mt-1 text-sm text-red-600">{errors.spanishSynopsis.message}</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <MultiSelect
            label={t.sports.fields.genres}
            options={sportsGenreOptions}
            value={watchedGenres}
            onChange={(newGenres) => setValue('genres', newGenres)}
            placeholder="Select genres..."
          />
          <MultiSelect
            label={t.tvChannels.fields.categories}
            placeholder={t.tvChannels.placeholders.categories}
            options={mapCategoriesToOptions(categoriesData || [])}
            value={selectedCategories}
            onChange={(newCategories) => {
              setSelectedCategories(newCategories);
              setValue('categoryIds', newCategories);
            }}
            error={errors.categoryIds?.message}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ImageUrlUploadField
            label={t.sports.fields.coverImage}
            placeholder={t.sports.placeholders.coverImage}
            value={watchedCoverImage || ''}
            onChange={(url) => setValue('coverImageUrl', url, { shouldValidate: true, shouldDirty: true })}
            error={errors.coverImageUrl?.message}
            disabled={isSubmitting}
            source={MediaSource.SPORT_POSTER}
          />

        </div>

        {/* Media Assets Section */}
        <MediaAssetsSection
          backgroundImageUrl={watchedBackgroundImage || ''}
          trailerUrl={watchedTrailerUrl || ''}
          onBackgroundImageChange={handleBackgroundImageChange}
          onTrailerUrlChange={handleTrailerUrlChange}
          backgroundImageSource={MediaSource.SPORT_BACKGROUND}
          backgroundImageError={errors.backgroundImageUrl?.message}
          trailerUrlError={errors.trailerUrl?.message}
        />

        <Input
          label={t.sports.fields.reference}
          placeholder={t.sports.placeholders.reference}
          error={errors.reference?.message}
          {...register('reference')}
        />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg">
          <Toggle
            label={t.sports.fields.active}
            checked={watch('active')}
            onChange={(checked) => setValue('active', checked)}
          />

          <Toggle
            label={t.sports.fields.adultContent}
            checked={watch('adultContent')}
            onChange={(checked) => setValue('adultContent', checked)}
          />

          <Toggle
            label={t.sports.fields.monoAudio}
            checked={watch('monoAudio')}
            onChange={(checked) => setValue('monoAudio', checked)}
          />

          <Toggle
            label={t.sports.fields.englishOnly}
            checked={watch('englishOnly')}
            onChange={(checked) => setValue('englishOnly', checked)}
          />

          <Toggle
            label={t.sports.fields.kidsContent}
            checked={watch('kidsContent')}
            onChange={(checked) => setValue('kidsContent', checked)}
          />
        </div>

        <LinkManager
          links={watchedLinks}
          onAddLink={handleAddLink}
          onUpdateLink={handleUpdateLink}
          onDeleteLink={handleDeleteLink}
          onToggleLink={handleToggleLink}
          error={errors.links?.message}
        />

        {submitError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            {t.buttons.cancel}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t.sports.actions.creating
              : isEdit
                ? t.sports.actions.edit
                : t.sports.actions.create
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SportsForm;
